import * as React from "react";
import {includes} from "lodash";
import {Panel, PanelBody, PanelFooter, PanelHeader} from "../../../shared/panel/Panel";
import {Button} from "../../../shared/button/Button";
import {ElementClasses, Form, FormProps, FormState} from "../../../../../shared/ts/components/form";
import {Input} from "../../../../../shared/ts/components/forms/input";
import {Range} from "../../../../../shared/ts/components/forms/range";
import {Select} from "../../../../../shared/ts/components/forms/select";
import {Data} from "../../../shared/cached_data";
import Icon from "../../../shared/icon/Icon";
import {RadioField} from "../../../../../shared/ts/components/forms/RadioField";
import {File, FileValue} from "../../../../../shared/ts/components/forms/file";
import {buttonTextOptions, offerPriceTypeOptions} from "../../../advertisments/helpers/constants";
import {ModalOpener} from "../../../advertisments/components/ModalOpener";
import {CreationType} from "../../CreationType";


interface IProps extends FormProps {
    handlePreview: () => void;
    submitAndGenerate: () => void;
    submitAndGenerateHtml: () => void;
    updateImageAfterCropping: (key: string, image?: string) => void;
    initialImage: string | Blob;
}

interface IState extends FormState {
    show_crop_v2_image: boolean;
}

export interface INativeCreationFormValues {
    mobile_name: string;
    mobile_address: string;
    mobile_region: string;
    mobile_vendor: string;
    mobile_construction_end_date: string;
    mobile_type: {
        value: string;
        option: {
            value: string;
            label: string;
        };
    };
    mobile_properties_count_for_sale: string;
    mobile_area: {lower: number, upper: number};
    mobile_ranges_rooms: {lower: string, upper: string};
    mobile_individual_price_m2: string;
    mobile_image_native: FileValue;
    mobile_button_text_type: number;
    desktop_vendor_logo: FileValue;
    desktop_distance: number;
}

export type INativeCreation = Record<keyof INativeCreationFormValues, string> & {
    id: number;
    offer: number;
    type: CreationType;
};

export class NativeCreationForm extends Form<IProps, IState> {
    public static elementClasses: ElementClasses = {
        // information fields
        "mobile_name": {
            type: Input,
            label: "Nazwa inwestycji"
        },
        "mobile_address": {
            type: Input,
            label: "Adres"
        },
        "mobile_region": {
            type: Input,
            label: "Region"
        },
        "mobile_vendor": {
            type: Input,
            label: "Deweloper"
        },
        // detail fields
        "mobile_construction_end_date": {
            type: Input,
            label: "Termin oddania"
        },
        "mobile_type": {
            type: Select,
            label: "Rodzaj nieruchomości"
        },
        "mobile_properties_count_for_sale": {
            type: Input,
            label: "Liczba nieruchomości"
        },
        "mobile_area": {
            type: Range,
            label: "Powierzchnia"
        },
        "mobile_ranges_rooms": {
            type: Range,
            label: "Liczba pokoi"
        },
        // price and image
        "mobile_individual_price_m2": {
            type: Input,
            label: "Cena indywidualna (zł/m2)"
        },
        "mobile_image_native": {
            type: File,
            label: "Zdjęcie"
        },
        "desktop_vendor_logo": {
            type: File,
            label: "Zdjęcie"
        },
        "mobile_button_text_type": {
            type: RadioField
        },
        "desktop_distance": {
            type: Input,
            label: "Odległość od centrum (km)"
        }
    };

    constructor(props: IProps) {
        super(props);
        this.state = {
            show_crop_v2_image: false
        };
    }

    private handleResettingImage = (e: React.MouseEvent, fileFieldName: "mobile_image_native" | "desktop_vendor_logo") => {
        e.preventDefault();
        this.triggerCrop(fileFieldName);
        this.props.updateImageAfterCropping(fileFieldName);
    };

    private triggerCrop = (name: "mobile_image_native" | "desktop_vendor_logo") => {
        // this is too slightly over complex now, but it used to handle 3 different size images.
        // let's leave it this way for the possible future case of coming back to multi sizes.
        // what it does is always using "mobile_image_native" as name
        const stateName = `show_crop_${name}` as keyof IState;
        this.setState({[stateName]: true} as Pick<IState, keyof IState>);
        // just needed a trigger
        window.setTimeout(() => this.setState({[stateName]: false} as Pick<IState, keyof IState>), 500);
    };

    private renderFile = (fileFieldName: "mobile_image_native" | "desktop_vendor_logo", width: number, height: number) => {
        const isInitialImage = this.props.values[fileFieldName] && includes(this.props.values[fileFieldName].selected, "main");
        const stateName = (this.state as any)[`show_crop_${fileFieldName}`];

        return (
            <div>
                <File {...this.generateProps(fileFieldName)}
                      allowedExtensions={["jpg", "jpeg", "png"]}
                      imgFilePreview
                      triggerCropOnFileChange={this.triggerCrop}
                      previewPath={this.props.values[fileFieldName].base64value}
                      label={`Do kreacji natywnej (${width}x${height})`}
                />

                <div className="psr t-5 mb-xl">
                    {this.props.values[fileFieldName].base64value && (
                        <ModalOpener
                            isInitialImage={isInitialImage}
                            name={fileFieldName}
                            width={width}
                            initialImage={this.props.initialImage}
                            image={this.props.values[fileFieldName]}
                            isOpen={stateName}
                            height={height}
                            updateImageAfterCropping={(image: string) => this.props.updateImageAfterCropping(fileFieldName, image)}
                        />
                    )}

                    {this.props.initialImage && (
                        <Button size="sm" color="primary-ghost" onClick={(e) => this.handleResettingImage(e, fileFieldName)}>
                            Pobierz ponownie zdjęcie
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    public render(): JSX.Element {
        const offerTypesOptions = Data.offer_types.getOptions().map((i: any) => ({...i, value: i.value.toString()}));
        return (
            <form>
                <div className="row">
                    <div className="col-xs-12">
                        <Panel color="default" className="df fd-col fg-1 fs-1 fb-a">
                            <PanelHeader className="fs22 fwl">
                                <div className="df df-row fai-center">
                                    <div className="fg-0">
                                        <div className="icon-holder fs20 psr bdrs-50p">
                                            <Icon className="vhc" icon="screen" />
                                        </div>
                                    </div>

                                    <div className="fg-1 panel-header-text">
                                        Kreacja natywna
                                    </div>
                                </div>
                            </PanelHeader>

                            <PanelBody>
                                <h3 className="fs16 fwb">Informacje o inwestycji</h3>

                                <p className="mb-xxxl w66 c-gray">
                                    Wybierając interesującą Cię inwestycje dane zostaną automatycznie załadowane bazując na aktualnych informacjach w ofercie. Możesz je edytować do indywidualnych potrzeb prowadzonej kampanii
                                </p>

                                <div className="row">
                                    <div className="col-xs-12 col-sm-12 col-sm-4">
                                        <Input {...this.generateProps("mobile_name")} />
                                    </div>

                                    <div className="col-xs-12 col-sm-12 col-sm-4">
                                        <Input {...this.generateProps("mobile_address")} />
                                    </div>

                                    <div className="col-xs-12 col-sm-12 col-sm-4">
                                        <Input {...this.generateProps("mobile_region")} />
                                    </div>
                                </div>

                                <div className="row mb-xl">
                                    <div className="col-xs-12 col-sm-4">
                                        <Input {...this.generateProps("mobile_vendor")} />
                                    </div>
                                </div>

                                <div className="bdbw-sm bdbs-solid bdbc-black-o10 mb-xxxl" />

                                <h3 className="fs16 mb-xl fwb">Szczegóły oferty</h3>

                                <div className="row mb-xl">
                                    <div className="col-xs-12 col-sm-6">
                                        <Input {...this.generateProps("mobile_construction_end_date")} />
                                    </div>

                                    <div className="col-xs-12 col-sm-3">
                                        <Select
                                            {...this.generateProps("mobile_type")}
                                            options={offerTypesOptions}
                                        />
                                    </div>

                                    <div className="col-xs-12 col-sm-3">
                                        <Input {...this.generateProps("mobile_properties_count_for_sale")} />
                                    </div>

                                    <div className="col-xs-12 col-sm-6">
                                        <Range {...this.generateProps("mobile_area")} type="number"/>
                                    </div>

                                    <div className="col-xs-12 col-sm-6">
                                        <Range {...this.generateProps("mobile_ranges_rooms")} type="number"/>
                                    </div>
                                </div>

                                <div className="bdbw-sm bdbs-solid bdbc-black-o10 mb-xxxl" />

                                <div className="row mb-xl">
                                    <div className="col-xs-6">
                                        <div>
                                            <h3 className="fs14 fwb mt-0">Ceny inwestycji</h3>

                                            <div className="row">
                                                <div className="col-xs-6">
                                                    <Input {...this.generateProps("mobile_individual_price_m2")}/>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="fs14 fwb mt-0">Odległość od centrum</h3>

                                            <div className="row">
                                                <div className="col-xs-6">
                                                    <Input type="number" {...this.generateProps("desktop_distance")}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-xs-6">
                                        <div>
                                            <h3 className="fs14 fwb mt-0">Zdjęcie inwestycji</h3>

                                            {this.renderFile("mobile_image_native", 1125, 633)}
                                        </div>
                                        <div>
                                            <h3 className="fs14 fwb mt-0">Zdjęcie dewelopera</h3>

                                            {this.renderFile("desktop_vendor_logo", 80, 60)}
                                        </div>
                                    </div>
                                </div>

                                <div className="bdbw-sm bdbs-solid bdbc-black-o10 mb-xxxl" />
                            </PanelBody>

                            <PanelFooter className="tar pv-xl">
                                <Button color="primary-ghost" size="lg" className="mr-xl" onClick={this.props.handlePreview}>Podgląd kreacji</Button>

                                <Button color="success" size="lg" className="mr-xl" onClick={this.props.onSubmit}>Zapisz kreację</Button>

                                <Button color="primary" size="lg" className="mr-xl" onClick={this.props.submitAndGenerate}>Zapisz i Wygeneruj kreację</Button>

                                <Button color="warning" size="lg" onClick={this.props.submitAndGenerateHtml}>Zapisz i Wygeneruj HTML</Button>
                            </PanelFooter>
                        </Panel>
                    </div>
                </div>
            </form>
        );
    }
}
